import { useState, useEffect, useMemo, type ReactNode } from 'react'
import TimeOffBalance from '../TimeOffBalance'
import { EmployeeInfo } from '../EmployeeInfo'
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import { LabeledValue } from '../../../components/common/LabeledValue'
import Table from '../../../components/common/Table/Table'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ValidationBanner from '../../../components/common/ValidationBanner'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner'
import Modal from '../../../components/common/Modal/Modal'
import VacationBidForm, { ReviewReadyRow } from './VacationBidForm'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { getTimeOffTypeLabel } from '../../../utils/timeOffTypes'
import { formatHours } from '../../../utils/formatters'
import { RootState, AppDispatch } from '../../../store/store'
import type { TimeOffRequestDetails } from '../../../store/slices/Requestor/timeOffDetailsSlice'
import useWithdrawRequest from '../../../hooks/useWithdrawRequest'
import WithdrawnView from '../../../pages/Requestor/StatusViews/detail/WithdrawnView'
import Loader from '../../../components/common/Loader'
import { setRequestorActiveTab } from '../../../store/slices/Requestor/requestorTabSlice'
import { isVacationBidOverlapForDate } from '../StatusViews/detail/constants'
import { formatDateToMMDDYY, convertMMDDYYYYToYYYYMMDD } from '../../../utils/date'

// Helper function to get hours for a specific date
const getDateHours = (hoursPerDay: string | Record<string, string>, dateStr: string): string => {
  if (typeof hoursPerDay === 'string') return hoursPerDay
  return hoursPerDay[dateStr] || '8'
}

const tableColumns = [
  { key: 'requestType', label: 'Request Type' },
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'timesRequested', label: 'Times Requested' },
  { key: 'hours', label: 'Hours' },
  { key: 'hoursOtherRequests', label: 'Day in Other Requests' },
]

/**
 * Renders the "Day in Other Requests" column as a disabled checkbox
 * (matching legacy's read-only checked/unchecked checkbox), and falls back
 * to the plain cell value for every other column.
 */
const renderOverlapCheckboxCell = (row: any, col: { key: string }): ReactNode => {
  if (col.key !== 'hoursOtherRequests') return row[col.key]
  if (row.id === 'totals-row') return ''
  return (
    <input
      type="checkbox"
      className="table-cell-checkbox"
      checked={!!row.hoursOtherRequests}
      disabled
      readOnly
      aria-label="Day in other requests"
    />
  )
}

/**
 * Extracts initial 0-indexed month from the first bid date (MM/DD/YYYY format).
 */
const getInitialViewedMonth = (firstDate?: string): number => {
  if (firstDate) {
    const parts = firstDate.split('/')
    if (parts.length === 3 && !Number.isNaN(Number(parts[0]))) {
      return Number(parts[0]) - 1
    }
  }
  return new Date().getMonth()
}

/**
 * Extracts initial year from the first bid date (MM/DD/YYYY format).
 */
const getInitialViewedYear = (firstDate?: string): number => {
  if (firstDate) {
    const parts = firstDate.split('/')
    if (parts.length === 3 && !Number.isNaN(Number(parts[2]))) {
      return Number(parts[2])
    }
  }
  return new Date().getFullYear()
}

/**
 * Transforms API other-request data into the tooltip structure expected by Datepicker.
 */
const buildOtherRequestTooltipDetails = (otherReqData: Record<string, any>): Record<string, any> => {
  const buildIndividualRequest = (detail: any) => ({
    startTime: detail.startTime,
    endTime: detail.endTime,
    allocations: detail.timeOffAllocations.map((alloc: any) => ({
      typeLabel: getTimeOffTypeLabel(alloc.toTypeCd),
      hours: Number(alloc.hours) || 0,
    })),
    totalHours: detail.timeOffAllocations.reduce((sum: number, alloc: any) => sum + Number(alloc.hours) || 0, 0),
  })

  const result: Record<string, any> = {}
  Object.entries(otherReqData).forEach(([dateKey, detailsArray]) => {
    if (!Array.isArray(detailsArray) || detailsArray.length === 0) return

    const [year, month, day] = dateKey.split('-')
    const formattedDate = `${month}/${day}/${year}`

    const firstDetail = detailsArray[0]
    const durationLabel = firstDetail.durationFlag === 'F' ? 'Full Day' : 'Partial Day'

    const tooltipAllocations = firstDetail.timeOffAllocations
      .filter(Boolean)
      .slice(0, 1)
      .map((alloc: any) => ({
        typeLabel: getTimeOffTypeLabel(alloc.toTypeCd),
        hours: Number(alloc.hours) || 0,
      }))

    const tooltipTotalHours = tooltipAllocations.reduce((sum: number, a: any) => sum + a.hours, 0)

    const tooltipEntry: any = {
      durationLabel,
      date: formattedDate,
      allocations: tooltipAllocations,
      totalHours: tooltipTotalHours,
      hasMultipleRequests: detailsArray.length > 1,
    }
    if (firstDetail.startTime) tooltipEntry.startTime = firstDetail.startTime
    if (firstDetail.endTime) tooltipEntry.endTime = firstDetail.endTime

    if (detailsArray.length > 1 && firstDetail.durationFlag === 'P') {
      tooltipEntry.individualRequests = detailsArray.map(buildIndividualRequest)
    }

    result[dateKey] = tooltipEntry
  })
  return result
}

const normalizeNuid = (value?: string | null): string => (value ?? '').trim().toLowerCase()

const extractNuidFromDisplay = (value?: string | null): string => {
  const trimmedValue = value?.trim() ?? ''
  const openParenIndex = trimmedValue.lastIndexOf('(')
  return openParenIndex >= 0 && trimmedValue.endsWith(')')
    ? trimmedValue.slice(openParenIndex + 1, -1).trim()
    : ''
}

const normalizeDisplayName = (value?: string | null): string => {
  const withoutProxyPrefix = value?.replace(/^Requestor Proxy\s+/i, '') ?? ''
  const openParenIndex = withoutProxyPrefix.lastIndexOf('(')
  const name = openParenIndex >= 0 ? withoutProxyPrefix.slice(0, openParenIndex) : withoutProxyPrefix
  return name.trim().replaceAll(/\s+/g, ' ').toLowerCase()
}

const getSubmittedAudit = (timeOffDetails: any) =>
  timeOffDetails?.auditHistory?.find((item: any) => item.toffReqstStatus === 'SUBMITTED')

const getOwnerNuid = (timeOffDetails: any): string =>
  timeOffDetails?.employee?.empNuid ||
  timeOffDetails?.employee?.nuid ||
  timeOffDetails?.empNUID ||
  timeOffDetails?.nuid ||
  ''

const getOwnerName = (timeOffDetails: any): string =>
  normalizeDisplayName(
    timeOffDetails?.employee?.empFullName ||
    timeOffDetails?.employee?.fullName ||
    [
      timeOffDetails?.employee?.empFirstName || timeOffDetails?.employee?.firstName,
      timeOffDetails?.employee?.empMiddleName || timeOffDetails?.employee?.middleName,
      timeOffDetails?.employee?.empLastName || timeOffDetails?.employee?.lastName,
    ].filter(Boolean).join(' ') ||
    timeOffDetails?.employeeName
  )

/**
 * Resolves the display label for the "Submitted By" metadata field.
 */
const resolveSubmittedBy = (timeOffDetails: any, isAdminMode: boolean): string => {
  let employee: any = {}
  try {
    employee = JSON.parse(sessionStorage.getItem('manualTimeOffEmployee') || '{}')
  } catch {
    employee = {}
  }
  const submittedBy = timeOffDetails?.submittedBy
  if (submittedBy?.startsWith('Requestor Proxy ')) return submittedBy

  if (submittedBy) {
    const submittedAudit = getSubmittedAudit(timeOffDetails)
    const actorNuid = normalizeNuid(
      extractNuidFromDisplay(submittedBy) ||
      submittedAudit?.lastUpdatedBy ||
      submittedAudit?.createdBy
    )
    const ownerNuid = normalizeNuid(getOwnerNuid(timeOffDetails))
    const actorName = normalizeDisplayName(submittedBy)
    const ownerName = getOwnerName(timeOffDetails)
    const isProxyActor =
      (actorNuid && ownerNuid && actorNuid !== ownerNuid) ||
      Boolean(timeOffDetails?.manualReqRcvdDate && actorName && ownerName && actorName !== ownerName)

    return isProxyActor ? `Requestor Proxy ${submittedBy}` : submittedBy
  }

  return isAdminMode && employee.admin?.adminName
    ? `Requestor Proxy ${employee.admin.adminName}`
    : (timeOffDetails?.submittedBy ?? '')
}

/**
 * Builds table rows from vacation bid entries.
 */
const buildRowsFromBids = (bids: any[], otherReqData: Record<string, any>, requestId: string) => {
  const rows: any[] = []
  bids.forEach((bid, bidIdx) => {
    bid.dates.forEach((dateStr: string, dateIdx: number) => {
      const dateObj = new Date(dateStr)
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' })
      const formattedDate = dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      const dateHours = getDateHours(bid.hoursPerDay, dateStr)
      rows.push({
        id: `${bidIdx}-${dateIdx}`,
        requestType: 'Vacation Bidding',
        date: formattedDate,
        day: dayName,
        timesRequested: 'Full Day',
        hours: dateHours,
        hoursOtherRequests: isVacationBidOverlapForDate(otherReqData, dateStr, requestId),
      })
    })
  })
  return rows
}

/**
 * Builds initial rows from incoming bids array or returns empty array.
 */
const getInitialDataRows = (
  incomingBids: any[] | undefined,
  otherReqData: Record<string, any>,
  requestId: string
) => (incomingBids && incomingBids.length > 0 ? buildRowsFromBids(incomingBids, otherReqData, requestId) : [])

/**
 * Merges updated review rows with existing table rows.
 */
const mergeUpdatedRows = (prev: any[], updatedRows: ReviewReadyRow[]) => {
  const affectedDates = new Set(updatedRows.map((r) => r.date))
  const kept = prev.filter((r) => !affectedDates.has(r.date))
  return [
    ...kept,
    ...updatedRows.map((r) => ({
      id: r.id,
      requestType: r.requestType,
      date: r.date,
      day: r.day,
      timesRequested: r.timesRequested,
      hours: r.hours,
      hoursOtherRequests: r.hoursOtherRequests,
    })),
  ]
}

/**
 * Appends the Total Hours summary row to the table data.
 */
const buildWithTotal = (dataRows: any[]) => {
  const totalHours = dataRows.reduce((sum, r) => sum + Number(r.hours || 0), 0)
  return [
    ...dataRows,
    { id: 'totals-row', requestType: 'Total Hours', date: '', day: '', timesRequested: '', hours: formatHours(totalHours), hoursOtherRequests: false, _totalRow: true },
  ]
}

/**
 * Parses MM/DD/YYYY string into a timestamp for sorting.
 */
const parseMMDDYYYY = (dateStr: string): number => {
  if (!dateStr) return 0
  const [month, day, year] = dateStr.split('/').map(Number)
  return new Date(year, month - 1, day).getTime()
}

/**
 * Builds metadata rows displayed in the system section.
 */
const buildSystemData = (isOnHold: boolean, timeOffDetails: any) => [
  ...(isOnHold
    ? [
        { id: 1, label: 'Hold Placed On', value: timeOffDetails?.holdPlacedOn },
        { id: 2, label: 'Hold Placed By', value: timeOffDetails?.holdPlacedBy },
      ]
    : []),
  { id: 3, label: 'Approver Comments', value: timeOffDetails?.approverComments },
  { id: 4, label: 'Request Rank', value: timeOffDetails?.requestRank == null ? 'NA' : String(timeOffDetails.requestRank) },
]

/**
 * Builds metadata rows displayed in the employee section.
 */
const buildEmployeeData = (timeOffDetails: any, requestId: string, submittedBy: string) => [
  { id: 0, label: 'Time Off Request ID', value: requestId },
  { id: 1, label: 'Submitted On', value: timeOffDetails?.submittedOn },
  { id: 2, label: 'Submitted By', value: submittedBy },
  { id: 3, label: 'Employee Comments', value: timeOffDetails?.employeeComments },
  ...(timeOffDetails?.manualReqRcvdDate
    ? [
        {
          id: 4,
          label: 'Manual Request Received Date',
          value: formatDateToMMDDYY(timeOffDetails.manualReqRcvdDate),
        },
      ]
    : []),
]

/**
 * Renders the submitted info banner or manual time off banner.
 */
const renderSubmittedBanner = (isAdminMode: boolean, isOnHold: boolean) => {
  if (isAdminMode) {
    return <ManualTimeOffBanner isSuccessPage className="w-full mt-2! py-[5px]!" />
  }
  return (
    <ValidationBanner
      variant="info"
      heading={<p className="p1 -medium">You have successfully submitted your Vacation Bid request.</p>}
      message={
        <p className="p3 -book mt-2">
          {isOnHold
            ? 'All Vacation Bidding requests are automatically placed On Hold by the system, and will be processed based on the vacation bidding policies.'
            : 'This vacation bidding request was submitted and is pending review by your approver.'}
        </p>
      }
      compact={false}
    />
  )
}

/**
 * Builds the TimeOffRequestDetails payload needed for the WithdrawnView component.
 */
const buildWithdrawnDetails = (
  timeOffDetails: any,
  requestId: string,
  sortedDataRows: any[],
  tableRowsWithTotal: any[],
  submittedBy: string
): TimeOffRequestDetails => {
  const requestOwnerName =
    timeOffDetails?.employee?.empFullName ||
    timeOffDetails?.employee?.fullName ||
    timeOffDetails?.employeeName ||
    ''
  const requestOwnerNuid =
    timeOffDetails?.employee?.empNuid ||
    timeOffDetails?.employee?.nuid ||
    timeOffDetails?.empNUID ||
    timeOffDetails?.nuid ||
    ''
  const submittedOn = timeOffDetails?.submittedOn || ''

  return {
    statuses: timeOffDetails?.statuses ?? [],
    auditHistory: timeOffDetails?.auditHistory ?? [],
    requestId,
    todate: sortedDataRows.at(-1)?.date || '',
    hoursRequested: tableRowsWithTotal.at(-1)?.hours || '',
    timeOffReqstType: 'Vacation Bidding',
    status: 'Withdrawn',
    detail: '',
    employeeName: requestOwnerName,
    nuid: requestOwnerNuid,
    submittedOn,
    submittedBy,
    employeeComments: timeOffDetails?.employeeComments,
    requestRank: timeOffDetails?.requestRank,
    manualReqRcvdDate: timeOffDetails?.manualReqRcvdDate,
    empNUID: requestOwnerNuid,
    employee: {
      ...timeOffDetails?.employee,
      nuid: requestOwnerNuid,
      empNuid: requestOwnerNuid,
      firstName: timeOffDetails?.employee?.firstName || timeOffDetails?.employee?.empFirstName || requestOwnerName,
      middleName: timeOffDetails?.employee?.middleName || timeOffDetails?.employee?.empMiddleName || null,
      lastName: timeOffDetails?.employee?.lastName || timeOffDetails?.employee?.empLastName || '',
      fullName: requestOwnerName,
      empFullName: requestOwnerName,
    },
    requestedDates: sortedDataRows.map((row) => ({
      requestType: 'A',
      date: convertMMDDYYYYToYYYYMMDD(row.date),
      day: row.day,
      times: row.timesRequested,
      requestedHours: row.hours,
      hoursInOtherRequests: row.hoursOtherRequests || 0,
    })),
  }
}

/**
 * Vacation bid request details view and withdrawal management page.
 */
const VacationBidDetails = () => {
  const navigate = useNavigate()
  const location = useLocation() as any
  const isAdminMode = location?.pathname?.startsWith?.('/admin') || false
  const dispatch = useDispatch<AppDispatch>()
  const otherReqData = useSelector((state: RootState) => state.otherReqDetails.data)
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [showWithdrawSuccess, setShowWithdrawSuccess] = useState(false)
  const [formDate, setFormDate] = useState<string | null>(null)
  const { withdraw, loading: withdrawLoading, error: withdrawError } = useWithdrawRequest()

  const firstBidDate = (location?.state?.vacationBids as any[] | undefined)?.[0]?.dates?.[0] as string | undefined
  const [viewedMonth, setViewedMonth] = useState(() => getInitialViewedMonth(firstBidDate))
  const [viewedYear, setViewedYear] = useState(() => getInitialViewedYear(firstBidDate))

  useEffect(() => {
    dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
  }, [viewedMonth, viewedYear, dispatch])

  const otherRequestDates = useMemo(() => {
    return Object.keys(otherReqData).map((dateStr) => {
      const [year, month, day] = dateStr.split('-')
      return `${month}/${day}/${year}`
    })
  }, [otherReqData])

  const otherRequestDetailsForTooltip = useMemo(() => {
    return buildOtherRequestTooltipDetails(otherReqData)
  }, [otherReqData])

  const handleMonthYearChange = (month: number, year: number) => {
    setViewedMonth(month)
    setViewedYear(year)
  }

  const timeOffDetails = location?.state?.timeOffDetails
  const requestId = timeOffDetails?.requestId || timeOffDetails?.timeOffRequestId || ''
  const isOnHold = (timeOffDetails?.status ?? '').toUpperCase() === 'SUBMITTED_ONHOLD'
  const submittedBy = resolveSubmittedBy(timeOffDetails, isAdminMode)

  const systemData = buildSystemData(isOnHold, timeOffDetails)
  const employeeData = buildEmployeeData(timeOffDetails, requestId, submittedBy)

  const incomingBids = location?.state?.vacationBids as any[] | undefined
  const initialDataRows = getInitialDataRows(incomingBids, otherReqData, requestId)

  const [localDataRows, setLocalDataRows] = useState(initialDataRows)
  const sortedDataRows = [...localDataRows].sort(
    (a, b) => parseMMDDYYYY(a.date) - parseMMDDYYYY(b.date)
  )
  const tableRowsWithTotal = buildWithTotal(sortedDataRows)
  const highlightedDates = sortedDataRows.map((r) => String(r.date)).filter(Boolean)
  const withdrawnTableRows = tableRowsWithTotal.map((row) => ({
    ...row,
    times: row.timesRequested,
    hoursInOtherRequests: row.hoursOtherRequests,
  }))

  const status = (timeOffDetails?.status ?? '').toUpperCase()
  const isDraft = status === 'DRAFT'

  useEffect(() => {
    if (incomingBids && incomingBids.length > 0) {
      setLocalDataRows(buildRowsFromBids(incomingBids, otherReqData, requestId))
    }
  }, [location?.state]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleReviewReady = (updatedRows: ReviewReadyRow[]) => {
    setLocalDataRows((prev) => mergeUpdatedRows(prev, updatedRows))
    setFormDate(null)
  }

  const handleWithdrawPrimary = async () => {
    const userNuid = sessionStorage.getItem('userId') || sessionStorage.getItem('nuid') || ''
    if (!requestId || !userNuid) {
      return
    }
    const success = await withdraw(requestId, userNuid)
    if (success) {
      setIsWithdrawOpen(false)
      setShowWithdrawSuccess(true)
    }
  }

  const handlePreviousNavigation = () => {
    dispatch(setRequestorActiveTab(3))
    navigate(`${isAdminMode ? '/admin' : ''}/requestor`, { state: { activeTab: 3 } })
  }

  const draftCalendarPrompt = formDate
    ? 'Fill in the form to update the selected date.'
    : 'Click a date below to add or edit your vacation bid for that day.'

  const withdrawnRequestDetails = buildWithdrawnDetails(
    timeOffDetails,
    requestId,
    sortedDataRows,
    tableRowsWithTotal,
    submittedBy
  )

  if (showWithdrawSuccess) {
    return (
      <div className="page-container">
        <BreadCrumbs
          currentPage="Request Detail"
          previousPage="TORT"
          onPreviousClick={handlePreviousNavigation}
        />
        {isAdminMode && <ManualTimeOffBanner className="w-[50%] mt-2! py-[5px]!" />}
        <WithdrawnView
          data={withdrawnRequestDetails}
          tableRows={withdrawnTableRows}
          highlightedDates={highlightedDates}
          showSuccessBanner
        />
        <TimeOffBalance />
        <EmployeeInfo />
      </div>
    )
  }

  return (
    <div className="page-container">
      <BreadCrumbs 
        currentPage="Request Detail" 
        previousPage="TORT" 
        onPreviousClick={handlePreviousNavigation} 
      />
      <h1 className="h1">{isOnHold ? 'Submitted Vacation Bid Request On Hold' : 'Submitted Vacation Bid Request'}</h1>

      <div className="flex items-start gap-6 mt-12!">
        <div className="w-1/2 pr-4 p-3 mb-0">
          {location?.state?.showSubmittedBanner && (
            <div className="mb-4">
              {renderSubmittedBanner(isAdminMode, isOnHold)}
            </div>
          )}
          {systemData.map((item) => <LabeledValue key={item.id} label={item.label} value={item.value} />)}
          <div className="mt-4">
            <button type="button" className="button -light" onClick={() => setIsWithdrawOpen(true)}>Withdraw Request</button>
          </div>
        </div>
        <div className="bg-[#F2F8FB] p-6! rounded mb-0 w-full md:w-1/2">
          {employeeData.map((item) => <LabeledValue key={item.id} label={item.label} value={item.value} />)}
        </div>
      </div>

      {withdrawLoading && <Loader />}
      {isWithdrawOpen && (
        <>
          <style>{`#modal-manual-trig .modal-save-btn{display:none !important;}`}</style>
          <Modal
            isOpen={isWithdrawOpen}
            title="Withdraw Request"
            onClose={() => setIsWithdrawOpen(false)}
            submitBtn="Withdraw Request"
            loading={withdrawLoading}
            backBtn="Cancel"
            onPrimary={handleWithdrawPrimary}
            onBack={() => setIsWithdrawOpen(false)}
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <img className="w-10 h-10 shrink-0" src="/assets/images/alertSolid.svg" alt="warning" />
              <div>
                <p className="p2 -semibold">You are requesting to withdraw this vacation bid request. It will be removed from the approvers queue.</p>
                {withdrawError && <p className="p3 -book text-red-600 mt-2">{withdrawError}</p>}
              </div>
            </div>
          </Modal>
        </>
      )}

      <hr className="-variation-1 mt-6" />

      {/* Current Request table */}
      <div className="mt-8!">
        <h2 className="h2">Current Request</h2>
        <div className="mt-4">
          <Table columns={tableColumns} rows={tableRowsWithTotal} renderCell={renderOverlapCheckboxCell} showPagination={false} caption="" />
        </div>
      </div>

      {/* Calendar + inline form split — same layout as CreateNewRequest */}
      <div className="mt-12!">
        <p className="p3 -book">
          {isDraft
            ? draftCalendarPrompt
            : 'View request details by clicking a specific date in the request.'}
        </p>
        <div className="flex gap-16! tablet:gap-8! mobile:gap-2! mt-4 items-start flex-col tablet:flex-row mobile:flex-col">
          <div className="w-full lg:w-auto">
            <Datepicker
              name=""
              openBehaviour="calendarView"
              value={highlightedDates}
              datesToBeHighlighted={highlightedDates}
              tableRows={tableRowsWithTotal}
              readOnly={!isDraft}
              onDateClick={isDraft ? (date) => setFormDate(date) : undefined}
              onChange={() => {/* controlled */}}
              onMonthYearChange={handleMonthYearChange}
              otherSelectedDatesToShow={otherRequestDates}
              otherRequestDetails={otherRequestDetailsForTooltip}
              calendarWidth={350}
              calendarHeight={350}
            />
          </div>
          <div className="w-full lg:flex-1">
            {isDraft && formDate !== null ? (
              <VacationBidForm
                selectedDates={[formDate]}
                onCancel={() => setFormDate(null)}
                hideReviewTable
                onReviewReady={handleReviewReady}
                currentRequestId={requestId}
              />
            ) : (
              <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
                {isDraft
                  ? 'Select a date on the calendar to add time to your request'
                  : 'View request details by clicking a specific date in the request.'}
              </p>
            )}
          </div>
        </div>
      </div>
      <TimeOffBalance />
      <EmployeeInfo />
    </div>
  )
}

export default VacationBidDetails
